import {createEventHandler} from '@wix/tpa-settings'
import {EVENTS_NAMESPACE, ExperimentNames, getFormId} from '@wix/wix-events-commons-statics'
import {ControllerParams, CreateControllerFn} from '@wix/yoshi-flow-editor'
import {watchInstance} from '../../commons/actions/instance'
import {DETAILS_ROUTE} from '../../commons/constants/navigation'
import {subscribeToStateChanges} from '../../commons/utils/store'
import {getPageUrl, isEditor3} from '../../commons/utils/wix-code-api'
import {getStore} from './controller/create-store'
import {setSlotData} from './controller/slots'
import {subscribeToConsentPolicyChanges} from './Widget/actions/consent-policy'
import {getExportedActions} from './Widget/actions/exported-actions'
import {addLoginListener} from './Widget/actions/members'
import {addLocationListener, navigate} from './Widget/actions/navigation'
import {DetailsPageProps} from './Widget/components/app/interfaces'
import {DetailsPageSettingsEventsKeys, DetailsSettingsNavigateActions, SettingsEvents} from './Widget/constants'
import {getEvent, getRegistrationStatusV3} from './Widget/selectors/event'

const createDetailsPageController: CreateControllerFn = async (controllerParams: ControllerParams) => {
  const componentEventHandler = createEventHandler<SettingsEvents>(
    controllerParams.controllerConfig.config.publicData.COMPONENT || {},
  )
  let store = null

  return {
    updateConfig: (_, newConfig) => {
      componentEventHandler.notify(newConfig.publicData.COMPONENT || {})
    },
    async pageReady() {
      const {wixCodeApi} = controllerParams.controllerConfig
      const controller = controllerParams.controllerConfig
      const pageUrl = await getPageUrl(wixCodeApi)

      const {flowAPI} = controllerParams

      store = await getStore(controllerParams)
      const state = store.getState()

      const formId = getFormId(getEvent(state))
      const useWixFormsEnabled = flowAPI.experiments.enabled(ExperimentNames.UseWixForms)

      if (formId && useWixFormsEnabled) {
        const {initFormController} = await import('@wix/form-viewer/controller')

        await initFormController(controllerParams.flowAPI, {
          formId,
          namespace: EVENTS_NAMESPACE,
          additionalMetadata: {
            registrationStatus: getRegistrationStatusV3(state),
          },
        })
      }

      setSlotData(controllerParams, state)

      store.dispatch(addLoginListener())

      subscribeToConsentPolicyChanges(store.dispatch, controllerParams.flowAPI)

      watchInstance(controller, store.dispatch)
      addLocationListener(controllerParams, store)

      const props: DetailsPageProps = {
        state,
        actions: getExportedActions(store),
        pageUrl,
        staticsBaseUrl: controller.appParams.baseUrls.staticsBaseUrl,
        // @ts-expect-error
        fitToContentHeight: true,
        pageReadyTimestamp: isEditor3(wixCodeApi) ? Date.now() : undefined,
      }
      controller.setProps(props)
      subscribeToStateChanges(controller, store)

      componentEventHandler.on(
        DetailsPageSettingsEventsKeys.Navigate,
        (navigateAction: DetailsSettingsNavigateActions) => {
          if (navigateAction === DetailsSettingsNavigateActions.details) {
            store.dispatch(navigate({route: DETAILS_ROUTE.DETAILS}))
          }
          if (navigateAction === DetailsSettingsNavigateActions.form) {
            store.dispatch(navigate({route: DETAILS_ROUTE.FORM}))
          }
        },
      )
    },
  }
}

export default createDetailsPageController
